<script>
import { get } from "lodash";

export default {
  name: "GiftSubscription",
  props: {
    sponsoredSubscription: {
      type: Object,
      required: true,
    },
    modifySubscriptionPeriodText: {
      type: Function,
      required: true
    },
    convertDate: {
      type: Function,
      required: true
    },
    changeSponsoredSubscriptionSettings: {
      type: Function,
      required: true
    },
    inviteRecipientToSponsoredSubscriptionSlot: {
      type: Function,
      required: true
    }
  },
  data() {
    return {
      validation_gift_subscription: false,
      email: '',
      show_invite_form: false,
      show_change_recipient_link: true,
    }
  },
  computed: {
    title() {
      return get(this.sponsoredSubscription, 'subscription.title', '');
    },
    price() {
      return get(this.sponsoredSubscription, 'subscription.price', 0);
    },
    currency() {
      return get(this.sponsoredSubscription, 'subscription.currency', '');
    },
    period() {
      return get(this.sponsoredSubscription, 'subscription.period', '');
    },
    active() {
      return this.sponsoredSubscription.activated_at !== null && this.sponsoredSubscription.expired_at === null;
    },
    hasNextSubscription() {
      return this.sponsoredSubscription.is_allowed_next_subscription === true;
    },
    expired() {
      return this.sponsoredSubscription.expired_at !== null;
    },
    subscriptionStatusBlocked() {
      return (this.active === false || this.sponsoredSubscription.is_paying_by_sponsor === false) || this.expired;
    },
    subscriptionStatus() {
      const has_next_subscription = get(this.sponsoredSubscription, 'subscription.next_subscription');
      if (has_next_subscription !== null) {
        return !!this.sponsoredSubscription.is_allowed_next_subscription;
      }
      const is_allowed_autorenew = get(this.sponsoredSubscription, 'subscription.is_allowed_autorenew');
      if (is_allowed_autorenew !== null) {
        return !!this.sponsoredSubscription.autorenew;
      }
    },
    recipientEmail() {
      const recipient_email = get(this.sponsoredSubscription, 'slots[0].recipient.email');
      if (recipient_email) {
        const first_name = get(this.sponsoredSubscription, 'slots[0].recipient.first_name');
        const last_name = get(this.sponsoredSubscription, 'slots[0].recipient.last_name');
        if (first_name && last_name) {
          return `${first_name} ${last_name} (${recipient_email})`;
        }
        return recipient_email;
      }
      const invited_email = get(this.sponsoredSubscription, 'slots[0].recipient_email');
      if (invited_email) return invited_email;
      return null;
    },
    showChangeRecipientLink() {
      if (this.active) return false;
      const invited_email = get(this.sponsoredSubscription, 'slots[0].recipient_email');
      if (invited_email === null) return false;
      return this.show_change_recipient_link;
    },
    showInviteForm() {
      if (this.active) return false;
      if (this.expired) return false;
      const invited_email = get(this.sponsoredSubscription, 'slots[0].recipient_email');
      if (invited_email === null) return true;
      return this.show_invite_form;
    },
    giftCode() {
      return get(this.sponsoredSubscription, 'slots[0].activation_code');
    }

  },
  methods: {
    getSubscriptionStatusSettings(value) {
      const has_next_subscription = get(this.sponsoredSubscription, 'subscription.next_subscription');
      if (has_next_subscription !== null) {
        return {is_allowed_next_subscription: value};
      }
      const is_allowed_autorenew = get(this.sponsoredSubscription, 'subscription.is_allowed_autorenew');
      if (is_allowed_autorenew !== null) {
        return {autorenew: value};
      }
    },
    async giftSubscription() {
      this.validation_gift_subscription = true;
      if (this.$refs.form_gift_subscription.checkValidity()) {

        const slot_id = _.get(this.sponsoredSubscription, 'slots[0].id');
        if (!slot_id) {
          console.error('Slot id is not found');
          return;
        }

        if (!this.email) {
          console.error('Email is required');
          return;
        }

        await this.inviteRecipientToSponsoredSubscriptionSlot(slot_id, this.email);
        this.validation_gift_subscription = false;
        this.email = '';
        this.show_invite_form = false;
        this.show_change_recipient_link = true;
      }
    },
    toggleInviteForm() {
      this.validation_gift_subscription = false;
      this.email = '';
      this.show_invite_form = !this.show_invite_form;
      this.show_change_recipient_link = !this.show_change_recipient_link;
    }
  }
}
</script>

<template>
  <div>

    <b-row>
      <b-col cols="5">
        <p class="text-secondary mb-1">Membership</p>
      </b-col>
      <b-col cols="7">
        <p class="mb-1" style="font-weight: 600;">{{ title }}</p>
      </b-col>
    </b-row>
    <b-row>
      <b-col cols="5">
        <p class="text-secondary mb-1">Terms</p>
      </b-col>
      <b-col cols="7">
        <p class="mb-1" v-if="price === 0">Free</p>
        <p class="mb-1" v-else>
          {{ (price / 100).toFixed(2) }}
          {{ currency }} / {{ modifySubscriptionPeriodText(period) }}
        </p>
      </b-col>
    </b-row>
    <b-row>
      <b-col cols="5">
        <p class="text-secondary mb-1">Active</p>
      </b-col>
      <b-col cols="7">
        <p class="mb-1">
          <span v-if="active" class="text-success text-uppercase">Yes</span>
          <span v-else class="text-uppercase">No</span>
        </p>
      </b-col>
    </b-row>
    <b-row>
      <b-col cols="5">
        <p class="text-secondary mb-1">Created</p>
      </b-col>
      <b-col cols="7">
        <p class="mb-1">
          {{ convertDate(sponsoredSubscription.created_at) }}
        </p>
      </b-col>
    </b-row>
    <b-row>
      <b-col cols="5">
        <p class="text-secondary mb-1">Activated</p>
      </b-col>
      <b-col cols="7">
        <p class="mb-1">
          {{ convertDate(sponsoredSubscription.activated_at) }}
        </p>
      </b-col>
    </b-row>
    <b-row>
      <b-col cols="5">
        <p class="text-secondary mb-1">Expires</p>
      </b-col>
      <b-col cols="7">
        <p class="mb-1">
          {{ convertDate(sponsoredSubscription.subscription_end_date) }}
        </p>
      </b-col>
    </b-row>

    <b-row>
      <b-col cols="5">
        <p class="text-secondary mb-1">Subscription status</p>
      </b-col>
      <b-col cols="7" class="d-flex flex-row align-items-center switch" :class="{'blocked': subscriptionStatusBlocked}">
        <b-btn
          :disabled="subscriptionStatusBlocked || subscriptionStatus"
          @click="changeSponsoredSubscriptionSettings(sponsoredSubscription.id, getSubscriptionStatusSettings(true))"
          variant="link"
          class="p-0 text-uppercase pb-1"
          :class="{'off': !subscriptionStatus, 'on': subscriptionStatus}"
        >
          <small class="flex-shrink-0">
            <span :class="{'text-success': !subscriptionStatusBlocked && subscriptionStatus}">Auto-renew</span>
          </small>
        </b-btn>
        <span class="mx-1">|</span>
        <b-btn
          :disabled="subscriptionStatusBlocked || !subscriptionStatus"
          @click="changeSponsoredSubscriptionSettings(sponsoredSubscription.id, getSubscriptionStatusSettings(false))"
          variant="link"
          class="p-0 text-uppercase pb-1"
          :class="{'off': subscriptionStatus, 'on': !subscriptionStatus}"
        >
          <small class="flex-shrink-0">
            <span :class="{'text-success': !subscriptionStatusBlocked && !subscriptionStatus}">Cancel</span>
          </small>
        </b-btn>
      </b-col>
    </b-row>

    <b-row>
      <b-col cols="5">
        <p class="text-secondary mb-1">Paid by sponsor</p>
      </b-col>
      <b-col cols="7" class="d-flex flex-row align-items-center switch" :class="{'blocked': expired}">
        <b-btn
          :disabled="sponsoredSubscription.is_paying_by_sponsor || expired"
          @click="changeSponsoredSubscriptionSettings(sponsoredSubscription.id, {is_paying_by_sponsor: true})"
          variant="link"
          class="p-0 text-uppercase pb-1"
          :class="{'off': !sponsoredSubscription.is_paying_by_sponsor, 'on': sponsoredSubscription.is_paying_by_sponsor}"
        >
          <small class="flex-shrink-0">
            <u v-if="!sponsoredSubscription.is_paying_by_sponsor || expired">On</u>
            <span class="text-success" v-else>On</span>
          </small>
        </b-btn>
        <span class="mx-1">|</span>
        <b-btn
          :disabled="!sponsoredSubscription.is_paying_by_sponsor || expired"
          @click="changeSponsoredSubscriptionSettings(sponsoredSubscription.id, {is_paying_by_sponsor: false})"
          variant="link"
          class="p-0 text-uppercase pb-1"
          :class="{'off': sponsoredSubscription.is_paying_by_sponsor, 'on': !sponsoredSubscription.is_paying_by_sponsor}"
        >
          <small class="flex-shrink-0">
            <u v-if="sponsoredSubscription.is_paying_by_sponsor && !expired">Off</u>
            <span v-else>Off</span>
          </small>
        </b-btn>
      </b-col>
    </b-row>

    <b-row>
      <b-col cols="5">
        <p class="text-secondary mb-1">Recipient</p>
      </b-col>
      <b-col cols="7">
        <p class="mb-0">{{ recipientEmail }}</p>
        <div v-if="showChangeRecipientLink" class="d-flex justify-content-end mt-n2">
          (<a @click.prevent="toggleInviteForm" href="#" class="action-link">change</a>)
        </div>
      </b-col>
    </b-row>

    <div v-if="showInviteForm" class="mb-2 mt-1">

      <b-form
        @submit.prevent="giftSubscription"
        novalidate ref="form_gift_subscription"
        class=""
        :class="{'was-validated' : validation_gift_subscription }">

        <!--Email-->
        <b-form-group class="mb-2 position-relative ">

          <b-form-input
            id="email"
            type="email"
            v-model.trim="email"
            required
            :class="[{'input-filled' : !!email}, 'rounded-0']">
          </b-form-input>

          <div class="invalid-feedback position-absolute mt-0 text-left">
            Please provide a valid value
          </div>

          <label for="last_name" class="label-animated text-secondary">Email <span
            class="text-danger">*</span></label>

        </b-form-group>

        <div class="d-flex justify-content-between">

          <div v-if="giftCode" class="d-flex align-items-center">
            <span class="text-secondary mr-1">Gift code: </span>
            <span class="font-weight-bold">{{ giftCode }}</span>
          </div>

          <div class="d-flex justify-content-end">
            <b-btn v-if="recipientEmail"
                   variant="link"
                   class="mr-1"
                   @click="toggleInviteForm">
              <u>cancel</u>
            </b-btn>

            <b-btn
              class="text-uppercase rounded-0 change-password-primary"
              variant="secondary"
              type="submit">
              Apply
            </b-btn>
          </div>
        </div>

      </b-form>

    </div>

  </div>
</template>



// WEBPACK FOOTER //
// src/components/GiftSubscription.vue